import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { getConteudoPublic, CONTEUDO_CHAVES } from '@/api/adminApi'
import { getClientPublicEntryPath } from '@/config/tenant'
import { useUser } from '@/contexts/UserContext'
import { isAdmin } from '@/utils/adminAuth'
import { isConteudoFlagEnabled, isConteudoFlagInData } from '@/utils/featureFlags'
import { readConteudoSnapshot, subscribeConteudoUpdated } from '@/utils/conteudoSync'
import styles from './MenuInicialPage.module.css'

type MenuEntry = {
  id: string
  label: string
  icon: string
  path: string
  show: boolean
  highlight?: boolean
}

const MENU_VISIBILITY_KEYS = [
  CONTEUDO_CHAVES.menu_show_quem_somos,
  CONTEUDO_CHAVES.menu_show_servicos,
  CONTEUDO_CHAVES.menu_show_contatos,
  CONTEUDO_CHAVES.menu_show_mapa,
  CONTEUDO_CHAVES.menu_show_galeria,
  CONTEUDO_CHAVES.menu_show_social,
  CONTEUDO_CHAVES.menu_show_minha_conta,
] as const

function hasMenuVisibilityData(data: Record<string, string | null>): boolean {
  return MENU_VISIBILITY_KEYS.every((key) => key in data)
}

type MenuFlagSetters = {
  setShowQuemSomos: (v: boolean) => void
  setShowServicos: (v: boolean) => void
  setShowContatos: (v: boolean) => void
  setShowMapa: (v: boolean) => void
  setShowGaleria: (v: boolean) => void
  setShowSocial: (v: boolean) => void
  setShowMinhaConta: (v: boolean) => void
}

function applyMenuFlagsStrict(data: Record<string, string | null>, setters: MenuFlagSetters) {
  setters.setShowQuemSomos(isConteudoFlagInData(data, CONTEUDO_CHAVES.menu_show_quem_somos))
  setters.setShowServicos(isConteudoFlagInData(data, CONTEUDO_CHAVES.menu_show_servicos))
  setters.setShowContatos(isConteudoFlagInData(data, CONTEUDO_CHAVES.menu_show_contatos))
  setters.setShowMapa(isConteudoFlagInData(data, CONTEUDO_CHAVES.menu_show_mapa))
  setters.setShowGaleria(isConteudoFlagInData(data, CONTEUDO_CHAVES.menu_show_galeria))
  setters.setShowSocial(isConteudoFlagInData(data, CONTEUDO_CHAVES.menu_show_social))
  setters.setShowMinhaConta(isConteudoFlagInData(data, CONTEUDO_CHAVES.menu_show_minha_conta))
}

/** Tenant antigo sem chaves de visibilidade: após o GET, trata omitido como visível. */
function applyMenuFlagsLegacy(data: Record<string, string | null>, setters: MenuFlagSetters) {
  setters.setShowQuemSomos(isConteudoFlagEnabled(data[CONTEUDO_CHAVES.menu_show_quem_somos]))
  setters.setShowServicos(isConteudoFlagEnabled(data[CONTEUDO_CHAVES.menu_show_servicos]))
  setters.setShowContatos(isConteudoFlagEnabled(data[CONTEUDO_CHAVES.menu_show_contatos]))
  setters.setShowMapa(isConteudoFlagEnabled(data[CONTEUDO_CHAVES.menu_show_mapa]))
  setters.setShowGaleria(isConteudoFlagEnabled(data[CONTEUDO_CHAVES.menu_show_galeria]))
  setters.setShowSocial(isConteudoFlagEnabled(data[CONTEUDO_CHAVES.menu_show_social]))
  setters.setShowMinhaConta(isConteudoFlagEnabled(data[CONTEUDO_CHAVES.menu_show_minha_conta]))
}

export function MenuInicialPage() {
  const navigate = useNavigate()
  const { logout, user } = useUser()
  const adminPreview = isAdmin() && !user

  const [bannerTexto, setBannerTexto] = useState('')
  const [bannerImagemUrl, setBannerImagemUrl] = useState<string | null>(null)
  /** Só mostra itens do menu depois de saber a visibilidade real (evita flash no F5). */
  const [menuReady, setMenuReady] = useState(false)
  const [showQuemSomos, setShowQuemSomos] = useState(false)
  const [showServicos, setShowServicos] = useState(false)
  const [showContatos, setShowContatos] = useState(false)
  const [showMapa, setShowMapa] = useState(false)
  const [showGaleria, setShowGaleria] = useState(false)
  const [showSocial, setShowSocial] = useState(false)
  const [showMinhaConta, setShowMinhaConta] = useState(false)

  const flagSetters = useMemo(
    () => ({
      setShowQuemSomos,
      setShowServicos,
      setShowContatos,
      setShowMapa,
      setShowGaleria,
      setShowSocial,
      setShowMinhaConta,
    }),
    [
      setShowQuemSomos,
      setShowServicos,
      setShowContatos,
      setShowMapa,
      setShowGaleria,
      setShowSocial,
      setShowMinhaConta,
    ]
  )

  const applyContent = useCallback(
    (data: Record<string, string | null>, opts?: { fromNetwork?: boolean }) => {
      setBannerTexto(data[CONTEUDO_CHAVES.menu_banner_texto] ?? '')
      setBannerImagemUrl(data[CONTEUDO_CHAVES.menu_banner_imagem_url] ?? null)
      if (hasMenuVisibilityData(data)) {
        applyMenuFlagsStrict(data, flagSetters)
        setMenuReady(true)
      } else if (opts?.fromNetwork) {
        applyMenuFlagsLegacy(data, flagSetters)
        setMenuReady(true)
      }
    },
    [flagSetters]
  )

  useEffect(() => {
    let cancelled = false
    const run = () => {
      if (cancelled) return
      const snap = readConteudoSnapshot()
      if (hasMenuVisibilityData(snap)) {
        applyContent(snap)
      }
      getConteudoPublic()
        .then((data) => {
          if (!cancelled) applyContent(data, { fromNetwork: true })
        })
        .catch(() => {
          if (!cancelled) {
            setBannerTexto('')
            setMenuReady(true)
          }
        })
    }
    run()
    const unsub = subscribeConteudoUpdated(run)
    return () => {
      cancelled = true
      unsub()
    }
  }, [applyContent])

  const menuItems: MenuEntry[] = [
    { id: 'quem', label: 'Quem somos', icon: '👥', path: '/quem-somos', show: showQuemSomos },
    { id: 'serv', label: 'Serviços', icon: '✨', path: '/servicos', show: showServicos },
    { id: 'cont', label: 'Contatos', icon: '📞', path: '/contatos', show: showContatos },
    { id: 'mapa', label: 'Mapa', icon: '📍', path: '/mapa', show: showMapa },
    { id: 'gal', label: 'Galeria', icon: '🖼️', path: '/galeria', show: showGaleria },
    { id: 'soc', label: 'Comunidade', icon: '💬', path: '/social', show: showSocial },
    {
      id: 'conta',
      label: 'Minha conta',
      icon: '👤',
      path: '/perfil',
      show: showMinhaConta,
      highlight: true,
    },
  ]

  const visibleItems = menuItems.filter((item) => item.show)

  return (
    <main className={styles.page}>
      <div className={styles.banner}>
        {bannerImagemUrl?.trim() ? (
          <img
            src={bannerImagemUrl}
            alt={bannerTexto || 'Banner'}
            className={styles.bannerImg}
          />
        ) : (
          <span className={styles.bannerPlaceholder}>{bannerTexto || 'Bem-vindo'}</span>
        )}
      </div>

      <section className={styles.menuSection}>
        <p className={styles.menuSectionLabel}>Explorar</p>
        <nav className={styles.menu} aria-label="Menu principal">
          {!menuReady ? (
            <>
              <div className={styles.menuSkeleton} aria-hidden />
              <div className={styles.menuSkeleton} aria-hidden />
              <div className={styles.menuSkeleton} aria-hidden />
            </>
          ) : visibleItems.length === 0 ? (
            <p className={styles.menuEmpty}>Nenhuma opção disponível no momento.</p>
          ) : (
            visibleItems.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`${styles.menuItem} ${item.highlight ? styles.menuItemHighlight : ''}`}
                onClick={() => navigate(item.path)}
              >
                <span className={styles.menuIcon} aria-hidden>
                  {item.icon}
                </span>
                <span className={styles.menuLabel}>{item.label}</span>
                <span className={styles.menuArrow} aria-hidden>
                  →
                </span>
              </button>
            ))
          )}
        </nav>
      </section>

      {!adminPreview && (
        <button
          type="button"
          className={styles.btnSair}
          onClick={() => {
            logout()
            window.location.replace(getClientPublicEntryPath())
          }}
        >
          Sair
        </button>
      )}
    </main>
  )
}
